import type {
  AddToQueue,
  BulkQueueResult,
  BulkScheduleResult,
  Contact,
  ContactList,
  ContactListQuery,
  CreateContact,
  ImportResult,
  InstantCall,
  InstantCallResult,
  QueueList,
  Salutation,
  ScheduleCalls,
  ScheduledCallList,
} from '@schwarz/contract';

import { apiSlice } from './api-slice.js';

/**
 * Contacts, the queue and scheduling (S05, S06).
 *
 * Everything that changes the queue invalidates `Contact` as well, because a
 * contact row carries `inQueue` and `scheduledFor` — the table's "Add to queue"
 * button reads them. Invalidating only `Queue` would leave the table offering to
 * queue somebody who is already queued until the next unrelated refetch.
 */
export const contactsApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getContacts: build.query<ContactList, ContactListQuery>({
      query: (params) => ({ url: '/contacts', params }),
      providesTags: ['Contact'],
    }),

    createContact: build.mutation<Contact, CreateContact>({
      query: (body) => ({ url: '/contacts', method: 'POST', body }),
      invalidatesTags: ['Contact'],
    }),

    /**
     * The inline control (S05: "saved immediately").
     *
     * Optimistically applied: a select that visibly snaps back to its old value
     * for the length of a round trip reads as a failure even when it succeeded.
     * The patch is undone if the request actually fails.
     */
    setSalutation: build.mutation<
      Contact,
      { id: string; salutation: Salutation; query: ContactListQuery }
    >({
      query: ({ id, salutation }) => ({
        url: `/contacts/${id}/salutation`,
        method: 'PATCH',
        body: { salutation },
      }),
      async onQueryStarted({ id, salutation, query }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          contactsApi.util.updateQueryData('getContacts', query, (draft) => {
            const row = draft.items.find((item) => item.id === id);
            if (row) row.salutation = salutation;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    setDoNotCall: build.mutation<Contact, { id: string; doNotCall: boolean }>({
      query: ({ id, doNotCall }) => ({
        url: `/contacts/${id}/do-not-call`,
        method: 'PATCH',
        body: { doNotCall },
      }),
      // Suppressing cancels queue entries and schedules server-side, so both
      // caches are stale afterwards.
      invalidatesTags: ['Contact', 'Queue'],
    }),

    deleteContact: build.mutation<{ ok: true }, string>({
      query: (id) => ({ url: `/contacts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Contact', 'Queue'],
    }),

    getQueue: build.query<QueueList, void>({
      query: () => ({ url: '/queue' }),
      providesTags: ['Queue'],
    }),

    addToQueue: build.mutation<BulkQueueResult, AddToQueue>({
      query: (body) => ({ url: '/queue', method: 'POST', body }),
      invalidatesTags: ['Queue', 'Contact'],
    }),

    removeFromQueue: build.mutation<{ ok: true }, string>({
      query: (id) => ({ url: `/queue/${id}`, method: 'DELETE' }),
      // Taking a timed entry out cancels the schedules behind it, so the
      // scheduled-calls cache is stale too.
      invalidatesTags: ['Queue', 'Contact', 'Scheduled'],
    }),

    clearQueue: build.mutation<{ removed: number }, void>({
      query: () => ({ url: '/queue', method: 'DELETE' }),
      invalidatesTags: ['Queue', 'Contact', 'Scheduled'],
    }),

    getScheduledCalls: build.query<ScheduledCallList, void>({
      query: () => ({ url: '/scheduled-calls' }),
      providesTags: ['Scheduled'],
    }),

    /**
     * Scheduling now writes a queue entry as well (the mock’s behaviour), so
     * `Queue` has to be invalidated or the panel beside the table — and the
     * sidebar count reading the same query — keeps showing the old queue until
     * something unrelated refetches it.
     */
    scheduleCalls: build.mutation<BulkScheduleResult, ScheduleCalls>({
      query: (body) => ({ url: '/scheduled-calls', method: 'POST', body }),
      invalidatesTags: ['Scheduled', 'Queue', 'Contact'],
    }),

    cancelScheduledCall: build.mutation<{ ok: true }, string>({
      query: (id) => ({ url: `/scheduled-calls/${id}`, method: 'DELETE' }),
      // Cancelling retimes or removes the queue entry the schedule created.
      invalidatesTags: ['Scheduled', 'Queue', 'Contact'],
    }),

    /**
     * CSV import (S05).
     *
     * The body is a `FormData`, which RTK Query passes through untouched —
     * deliberately without a Content-Type, so the browser can set the multipart
     * boundary. Setting the header by hand omits the boundary and the server
     * sees a body it cannot split.
     */
    importContacts: build.mutation<ImportResult, File>({
      query: (file) => {
        const body = new FormData();
        body.append('file', file);
        return { url: '/imports', method: 'POST', body };
      },
      invalidatesTags: ['Contact'],
    }),

    /**
     * Call the selected contacts now (S07).
     *
     * Invalidates `Contact` because a placed call flips the row to DIALING, and
     * `Queue` because the dialler consumes a queue entry when it dials one. Not
     * optimistic: an instant call can be refused for four different reasons, and
     * showing a row as calling before the server agrees would mean taking it back
     * again a moment later.
     */
    placeInstantCalls: build.mutation<InstantCallResult, InstantCall>({
      query: (body) => ({ url: '/calls/instant', method: 'POST', body }),
      invalidatesTags: ['Contact', 'Queue'],
    }),
  }),
});

export const {
  useGetContactsQuery,
  useCreateContactMutation,
  useSetSalutationMutation,
  useSetDoNotCallMutation,
  useDeleteContactMutation,
  useGetQueueQuery,
  useAddToQueueMutation,
  useRemoveFromQueueMutation,
  useClearQueueMutation,
  useGetScheduledCallsQuery,
  useScheduleCallsMutation,
  useCancelScheduledCallMutation,
  useImportContactsMutation,
  usePlaceInstantCallsMutation,
} = contactsApi;
